import { AppNotificationsManager } from 'app/components/AppNotificationsManager';
import { AppRoutes } from 'app/components/routes';
import { axiosInstance } from 'services/api';
import { useAuth } from 'context';
import {
  firebaseConfig,
  getFirebaseToken,
  isFirebaseSupported,
  onMessageListener,
} from 'firebaseConfig/firebase';
import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { PushNotificationContext } from './components/ReactContexts/pushNotificationContext';
import ScrollToTop from './widgets/ScrollToTop';

const generateUUID = () => {
  // Public Domain/MIT
  var d = new Date().getTime(); //Timestamp
  var d2 =
    (typeof performance !== 'undefined' &&
      performance.now &&
      performance.now() * 1000) ||
    0; //Time in microseconds since page-load or 0 if unsupported
  return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    var r = Math.random() * 16; //random number between 0 and 16
    if (d > 0) {
      //Use timestamp until depleted
      r = (d + r) % 16 | 0;
      d = Math.floor(d / 16);
    } else {
      //Use microseconds since page-load if supported
      r = (d2 + r) % 16 | 0;
      d2 = Math.floor(d2 / 16);
    }
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

export function App() {
  const { isAuthenticated } = useAuth();

  // it's a temporary solution until equipment fetching is replated with RTK Query
  // usePollingEquipmentData();

  // This effect will reload page when the page is loaded from the bfcache
  // Refrence: https://web.dev/articles/bfcache
  React.useEffect(() => {
    // Define an event handler for the 'pageshow' event
    const handlePageShow = (event: PageTransitionEvent): void => {
      // If the page is loaded from the bfcache (back-forward cache), reload to ensure fresh content
      if (event.persisted) {
        window.location.reload();
      }
    };
    // Add the event listener when the component mounts
    window.addEventListener('pageshow', handlePageShow);
    // Cleanup function: remove the event listener when the component unmounts
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []); // Empty dependency array ensures this effect runs only once on mount

  const { i18n } = useTranslation();

  const { addPushNotification, pushNotifications } = React.useContext(
    PushNotificationContext,
  );
  const fcmCall = React.useRef<boolean>(false);
  const deleteCall = React.useRef<boolean>(false);

  onMessageListener()
    .then(payload => {
      if (payload.data?.messageId) {
        addPushNotification(payload.data?.messageId);
      }
    })
    .catch(err => console.log('failed: ', err));

  React.useEffect(() => {
    (async () => {
      if (!(await isFirebaseSupported())) {
        console.log(
          'Firebase push notification feature is not supported on this browser.',
        );
        return;
      }

      const deviceId = window.localStorage.getItem('kubota_deviceId');
      if (Notification?.permission === 'granted') {
        navigator.serviceWorker.onmessage = event => {
          if (event.data.messageType === 'notification-clicked') {
            if (event?.data?.data?.messageId) {
              addPushNotification(event?.data?.data?.messageId);
            }
          }
        };
      } else if (deviceId && !deleteCall.current && isAuthenticated) {
        deleteCall.current = true;
        axiosInstance
          .delete(`/notification/fcm-token/${deviceId}`)
          .then(result => {
            if (result.status == 200) {
              console.log('deleted succesfully');
              window.localStorage.removeItem('kubota_deviceId');
            }
          });
      }
    })();
  }, []);

  React.useEffect(() => {
    (async () => {
      if (!(await isFirebaseSupported())) {
        console.log(
          'Firebase push notification feature is not supported on this browser.',
        );
        return;
      }

      if (
        (window.location.hash.includes('askNotificationPermission') ||
          Notification?.permission === 'granted') &&
        !fcmCall.current &&
        !window.localStorage.getItem('kubota_deviceId') &&
        isAuthenticated
      ) {
        fcmCall.current = true;
        const swReg = await navigator.serviceWorker.register(
          `/firebase-messaging-sw.js?${new URLSearchParams({
            ...firebaseConfig,
          } as any).toString()}`,
        );

        const deviceId = `web${generateUUID()}`;
        const fcmReg = async token => {
          await axiosInstance
            .post(
              `/notification/fcm-token?${new URLSearchParams({
                token,
                platform: 'web',
                deviceId,
              } as any).toString()}`,
            )
            .then(res => {
              window.localStorage.setItem('kubota_deviceId', deviceId);
            })
            .catch(ex => console.log(ex));
        };
        await getFirebaseToken(swReg, fcmReg);
      }
    })();
  });

  React.useEffect(() => {
    if (window.location.hash.startsWith('#/equipment/?messageId=')) {
      const messageId = window.location.hash.slice(-36);
      if (!pushNotifications.includes(messageId)) {
        addPushNotification(messageId);
      }
    }
  }, [pushNotifications]);

  return (
    <>
      <ScrollToTop />

      <Helmet
        titleTemplate="%s - myKubota"
        defaultTitle="myKubota"
        htmlAttributes={{ lang: i18n.language }}
      >
        <meta name="description" content="muKubota web application" />
      </Helmet>

      <AppNotificationsManager />

      {/* <KeepDataClean /> */}

      <AppRoutes />
    </>
  );
}
